// Libraries
import { Box, Grid, TextField, Typography } from "@mui/material";
import clsx from "clsx";
import { useState } from "react";

// Components
import LabeledFormInput from "@/Layouts/components/SmartAddressFormSection/LabeledFormInput";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";

// Helpers
import { sortDropdownOptions } from "../../../helpers";

// Types
import { TSelectValue, TType } from "../../../types";

// Context
import { useLeadsContext } from "@/DataPages/context/crm/LeadsContext";

type TAdditionalDetailsSectionProps = {
	salesPersonData: {
		data: TType[];
		loading: boolean;
	};
	bdcUsersData: {
		data: TType[];
		loading: boolean;
	};
	managerUsersData: {
		data: TType[];
		loading: boolean;
	};
};

export default function AdditionalDetailsSection(props: Readonly<TAdditionalDetailsSectionProps>) {
	const { salesPersonData, bdcUsersData, managerUsersData } = props;
	const { data: salesPersonOptions, loading: salesPersonLoading } = salesPersonData;
	const { data: bdcUsersOptions, loading: bdcUsersLoading } = bdcUsersData;
	const { data: managerUsersOptions, loading: managerUsersLoading } = managerUsersData;

	const { isSavingLead, getContextError, getContextValue, onChangeContext } = useLeadsContext();

	const [notes, setNotes] = useState<string>(getContextValue("prospect", "notes") as string);

	return (
		<Box>
			<Typography className="!font-montserrat !font-[500] !text-[16px] !text-gray-text !mb-3">
				Additional Details (optional)
			</Typography>

			<Grid container rowSpacing={1.5} spacing={3} className="!mb-3">
				<Grid item xs={12} sm={4}>
					<LabeledFormInput
						label="Secondary Salesperson"
						formInput={
							<SmartSelector
								selectBy={"type"}
								showBy={"type"}
								options={sortDropdownOptions(salesPersonOptions)}
								disabledOptions={
									getContextValue("prospect", "salesPerson") ? [getContextValue("prospect", "salesPerson") as string] : []
								}
								selected={getContextValue("prospect", "secondarySalesPerson") as unknown as TSelectValue}
								popperProps={{ sx: { zIndex: `${1300}!important` } }}
								onChangeAction={(value) =>
									onChangeContext("prospect", "secondarySalesPerson", (value as unknown as TType)?.type ?? "")
								}
								error={getContextError("prospect", "secondarySalesPerson")}
								inputProps={{
									placeholder: "Secondary Salesperson",
								}}
								clearable
								loading={salesPersonLoading}
								disabled={isSavingLead}
							/>
						}
					/>
				</Grid>

				<Grid item xs={12} sm={4}>
					<LabeledFormInput
						label="BDC User"
						formInput={
							<SmartSelector
								selectBy={"type"}
								showBy={"type"}
								options={sortDropdownOptions(bdcUsersOptions)}
								selected={getContextValue("prospect", "bdcUser") as unknown as TSelectValue}
								popperProps={{ sx: { zIndex: `${1300}!important` } }}
								onChangeAction={(value) => onChangeContext("prospect", "bdcUser", (value as unknown as TType)?.type ?? "")}
								error={getContextError("prospect", "bdcUser")}
								inputProps={{
									placeholder: "BDC User",
								}}
								clearable
								loading={bdcUsersLoading}
								disabled={isSavingLead}
							/>
						}
					/>
				</Grid>

				<Grid item xs={12} sm={4}>
					<LabeledFormInput
						label="Manager"
						formInput={
							<SmartSelector
								selectBy={"type"}
								showBy={"type"}
								options={sortDropdownOptions(managerUsersOptions)}
								selected={getContextValue("prospect", "manager") as unknown as TSelectValue}
								popperProps={{ sx: { zIndex: `${1300}!important` } }}
								onChangeAction={(value) => onChangeContext("prospect", "manager", (value as unknown as TType)?.type ?? "")}
								error={getContextError("prospect", "manager")}
								inputProps={{
									placeholder: "Manager",
								}}
								clearable
								loading={managerUsersLoading}
								disabled={isSavingLead}
							/>
						}
					/>
				</Grid>

				<Grid item xs={12}>
					<LabeledFormInput
						label="Notes"
						formInput={
							<TextField
								id="outlined-textarea"
								variant="standard"
								placeholder="Write Notes Here"
								multiline
								fullWidth
								rows={"7"}
								inputProps={{ maxLength: 10000 }}
								InputProps={{
									disableUnderline: true,
									className: clsx({
										"!w-full !h-[172px] !bg-white !items-baseline": true,
										"!font-sans !text-[14.5] !text-default rounded-md font-normal": true,
										"!border border-[#CCC]": true,
										"!px-2 !py-1": true,
									}),
								}}
								value={notes}
								onChange={(e) => setNotes(e.target.value ?? "")}
								onBlur={() => onChangeContext("prospect", "notes", notes ?? "")}
								disabled={isSavingLead}
							/>
						}
					/>
				</Grid>
			</Grid>
		</Box>
	);
}
